import { cleanMatchText } from './playerName';

/**
 * Narrative Fan-Out and Commentary Key Generation Utilities
 * 
 * This module handles fanning out rich narrative commentary events into
 * separate sub-events with proper sequencing and unique key generation.
 */

/**
 * Generates unique keys for commentary events to ensure proper React rendering
 * @param {object} event - Commentary event object
 * @returns {string} - Unique key for the event
 */
export function getCommentaryKey(event) {
  // Use _fanout_key if already set (for fanned-out events)
  if (event._fanout_key) {
    return event._fanout_key;
  }

  // Build base key using stable server or hydration fields before the minute fallback
  let baseKey;
  
  if (event.sequence_number != null) {
    baseKey = `seq-${event.sequence_number}`;
  } else if (event.timestamp) {
    baseKey = `time-${new Date(event.timestamp).getTime()}`;
  } else if (event._id != null) {
    baseKey = `id-${String(event._id)}`;
  } else if (event._history_index != null) {
    baseKey = `history-${event._history_index}`;
  } else {
    baseKey = `min-${event.minute || 0}`;
  }

  // Concatenate either live or saved event type for simultaneous events
  const eventType = event.event_type || event.type || 'UNKNOWN';
  return `${baseKey}-${eventType}`;
}

/**
 * Fans out narrative events into separate sub-events for sequential rendering
 * @param {array|object} events - Single event or array of events to fan out
 * @returns {array} - Array of fanned-out events
 */
export function fanOutNarrative(events) {
  const eventArray = Array.isArray(events) ? events : [events];
  const fannedOutEvents = [];

  eventArray.forEach((event) => {
    // If event doesn't have narrative, return as-is
    if (!event.narrative || typeof event.narrative !== 'object') {
      fannedOutEvents.push(event);
      return;
    }

    const { narrative } = event;
    const baseEvent = { ...event };
    delete baseEvent.narrative; // Remove narrative from base event

    // Define sub-types in order they should appear
    const subTypes = [
      { key: 'buildup', text: narrative.buildup },
      { key: 'action', text: narrative.action },
      { key: 'outcome', text: narrative.outcome },
      { key: 'aftermath', text: narrative.aftermath }
    ];

    // Stripped: Do NOT fan-out narrative.descriptors into a separate sub-event.
    // Emitting them as standalone events causes duplicate refreshes when resync
    // batches arrive. They ride along on each sub-event as `_descriptors` so the
    // feed can render them as chips without another wire message.
    const descriptors = Array.isArray(narrative.descriptors)
      ? narrative.descriptors.filter(Boolean)
      : null;

    let hasAnySubEvent = false;

    // Create sub-events for each non-empty narrative part
    subTypes.forEach((subType) => {
      const messageText = cleanMatchText(subType.text);
      if (messageText) {
        const subEvent = {
          ...baseEvent,
          message_text: messageText,
          sub_type: subType.key, // Phase 4: Add explicit sub_type flag
          sequence_number: baseEvent.sequence_number, // Preserve server sequence_number
          timestamp: baseEvent.timestamp, // Preserve server timestamp
          _fanout_key: `${getCommentaryKey(event)}-${subType.key}`,
          unique_id: `${event._id || event.id || getCommentaryKey(event)}-${subType.key}`, // For React keys
          ...(descriptors && descriptors.length ? { _descriptors: descriptors } : {})
        };

        fannedOutEvents.push(subEvent);
        hasAnySubEvent = true;
      }
    });

    // If no sub-events were created, add the original event
    if (!hasAnySubEvent) {
      fannedOutEvents.push(event);
    }
  });

  return fannedOutEvents;
}

export default {
  getCommentaryKey,
  fanOutNarrative
};
